import { usePagination } from '@mantine/hooks'
import { useEffect, useMemo, useState } from 'react'

import {
  getInitialPageFromSearchParams,
  getInitialPerPageFromSearchParams,
  syncPaginationToQueryParams,
} from './query-params-handling'
import {
  UseControlledPagination,
  UsePaginationParams,
} from './use-controlled-pagination'

const useControlledPagination = ({
  total,
  siblings,
  boundaries,
  initialPerPage = 50,
  noQueryParamSync = false,
  selectionState,
}: UsePaginationParams): UseControlledPagination => {
  const [controlledTotal, setControlledTotal] = useState(total)

  const initialPagiationFromQueryParams = useMemo(() => {
    return {
      page: noQueryParamSync ? 1 : getInitialPageFromSearchParams() ?? 1,
      perPage: noQueryParamSync
        ? initialPerPage
        : getInitialPerPageFromSearchParams() ?? initialPerPage,
    }
  }, [])

  useEffect(() => {
    if (!noQueryParamSync) {
      syncPaginationToQueryParams(initialPagiationFromQueryParams)
    }
  }, [])

  const [page, setPage] = useState(initialPagiationFromQueryParams.page)
  const [perPage, setPerPage] = useState(
    initialPagiationFromQueryParams.perPage,
  )

  const pageSetter = (pageNumber: number): void => {
    if (!noQueryParamSync) {
      syncPaginationToQueryParams({ page: pageNumber, perPage })
    }

    if (selectionState) {
      selectionState.setSelectedRows([])
    }

    setPage(pageNumber)
  }

  const perPageSetter = (perPage: number): void => {
    if (!noQueryParamSync) {
      syncPaginationToQueryParams({ page: 1, perPage })
    }

    if (selectionState) {
      selectionState.setSelectedRows([])
    }
    setPerPage(perPage)
    setPage(1)
  }

  const pagination = usePagination({
    total: controlledTotal,
    page,
    onChange: pageSetter,
    siblings,
    boundaries,
  })

  return {
    ...pagination,
    setTotal: setControlledTotal,
    total: controlledTotal,
    perPage,
    setPerPage: perPageSetter,
    setPage: pageSetter,
  }
}

export default useControlledPagination
